/* Phase 2 primitive: the one HMS loading skeleton.
 *
 * The shimmer block that stands in for content while it loads. Screens
 * currently reach for a spinner for everything, which tells the user
 * "wait" but not "what is coming"; a skeleton keeps the layout stable so
 * nothing jumps when the data lands.
 *
 * Props:
 *  - shape:  'text'   height 0.8em, full width unless `width` says else
 *            'title'  height 1.1em, 40% wide
 *            'rect'   sized by `width` / `height`
 *            'circle' sized by `size` (avatars, status dots)
 *  - width / height / size: number (px) or any CSS length
 *  - lines:  convenience — render n stacked text rows instead of one
 *            block, the last one short so it reads as a paragraph
 *  - className: extra hook
 *
 * Accessibility: the shimmer blocks are decorative and every one of them
 * is aria-hidden. The wrapper carries role="status" aria-label="Loading"
 * so assistive tech gets ONE announcement for the whole group instead of
 * reading out a forest of empty boxes.
 *
 * The shimmer also stops entirely under `prefers-reduced-motion` — see
 * the stylesheet. A sweeping highlight is exactly the kind of repeating
 * motion that setting exists to kill.
 */
import React from 'react';
import './HMSSkeleton.css';

// Bare numbers are pixels; anything else is passed through as written.
const len = (value) => (typeof value === 'number' ? `${value}px` : value);

const SHAPE_CLASS = {
  text: 'hms-skeleton-text',
  title: 'hms-skeleton-title',
  rect: 'hms-skeleton-rect',
  circle: 'hms-skeleton-circle',
};

const HMSSkeleton = ({
  shape = 'text',
  width,
  height,
  size,
  lines,
  className = '',
}) => {
  const blockStyle = () => {
    const style = {};
    if (shape === 'circle') {
      if (size !== undefined) {
        style.width = len(size);
        style.height = len(size);
      }
    } else {
      if (width !== undefined) style.width = len(width);
      if (height !== undefined) style.height = len(height);
    }
    return Object.keys(style).length ? style : undefined;
  };

  const blockClass = [
    'hms-skeleton',
    SHAPE_CLASS[shape] || SHAPE_CLASS.text,
  ].join(' ');

  return (
    <span
      className={`hms-skeleton-group ${className}`.trim()}
      role="status"
      aria-label="Loading"
    >
      {lines
        ? Array.from({ length: lines }, (unused, index) => (
            <span
              key={index}
              className="hms-skeleton hms-skeleton-text"
              /* Last row short, so a stack of them reads as a paragraph
                 rather than a solid slab. */
              style={index === lines - 1 ? { width: '60%' } : undefined}
              aria-hidden="true"
            />
          ))
        : <span className={blockClass} style={blockStyle()} aria-hidden="true" />}
    </span>
  );
};

export default HMSSkeleton;
